import { clampValue } from '@/utils/numbers';
import type { AiModelData } from '@/types/nodes/model';
import type { CameraMode, Dimensions, NodeId } from 'web';
import type { FlowGraph } from '../FlowGraph';

const MIN_DIMENSION = 1;
const MAX_DIMENSION = 8192;

export class ThreeDView {
  constructor(
    private graph: FlowGraph,
    private nodeId: NodeId,
  ) {}

  setCameraLocked(locked: boolean) {
    this.graph.wasmEdit((wasm) => wasm.setCameraLocked(this.nodeId, locked));
  }

  setCameraMode(mode: CameraMode) {
    this.graph.wasm.call((wasm) => wasm.setCameraMode(this.nodeId, mode));
  }

  setShowGrid(showGrid: boolean) {
    this.graph.wasm.call((wasm) => wasm.setShowGrid(this.nodeId, showGrid));
  }

  setFov(fov: number, ongoing = false) {
    const clampedFov = clampValue(fov, 1, 179);
    const data = this.graph.store.getNodeData<AiModelData>(this.nodeId);
    const selectedGeneration = data.generations[data.selectedIndex];
    const currentFov =
      selectedGeneration?.kind.type === '3D' ? (selectedGeneration.kind.cameraOptions?.fov ?? 75) : undefined;

    if (currentFov === clampedFov && (ongoing || !this.graph.hasOngoingAction())) {
      return;
    }

    this.graph.wasmEdit((wasm) => wasm.setCameraFov(this.nodeId, clampedFov), ongoing);
  }

  setDimensions({ width, height }: Dimensions) {
    const clampedWidth = clampValue(Math.round(width), MIN_DIMENSION, MAX_DIMENSION);
    const clampedHeight = clampValue(Math.round(height), MIN_DIMENSION, MAX_DIMENSION);
    const data = this.graph.store.getNodeData<AiModelData>(this.nodeId);
    const selectedGeneration = data.generations[data.selectedIndex];
    const viewport =
      selectedGeneration?.kind.type === '3D' ? selectedGeneration.kind.cameraOptions?.viewport : undefined;

    if (
      (viewport?.width ?? data.renderWidth) === clampedWidth &&
      (viewport?.height ?? data.renderHeight) === clampedHeight
    ) {
      return;
    }

    this.graph.wasmEdit((wasm) => wasm.setDimensions(this.nodeId, clampedWidth, clampedHeight));
  }

  resetCamera() {
    this.graph.wasm.call((wasm) => wasm.resetCamera(this.nodeId));
  }
}
